import { Badge, Card, Typography } from 'components';
import useGetKitIdParam from 'hooks/useGetKitIdParam';
import { useMemo } from 'react';
import useSubMetricEvaluations from 'services/insights/useSubMetricEvaluations';
import useCategorySectionEvaluations from 'services/kits/useCategorySectionEvaluations';
import useKitSummaryScores from 'services/kits/useKitSummaryScores';
import type { MainFinding } from 'views/Results/Summary/SummaryCards/NoteCard/useMainFindings';
import {
  calculateNavigationBadge,
  countEvaluationsByCategoryName,
  getProNavigationBadge,
  getSectionEvaluationBadge,
  PRO_CATEGORY_NAME,
  sortSectionEvaluationsByStatusAndPriority,
} from 'views/Results/utils/categoriesAndMetrics';
import styles from './MicrobiomeHealthScoresCards.module.css';

interface MicrobiomeHealthScoresCardsProps {
  mainFindings: MainFinding[];
}

export default function MicrobiomeHealthScoresCards({
  mainFindings,
}: MicrobiomeHealthScoresCardsProps) {
  const kitId = useGetKitIdParam();
  const sectionEvaluations = useCategorySectionEvaluations(kitId);
  const { data: metrics } = useSubMetricEvaluations();

  const evaluationCountsByCategory = useMemo(
    () => countEvaluationsByCategoryName(metrics),
    [metrics],
  );

  const sortedEvaluations = sortSectionEvaluationsByStatusAndPriority(
    sectionEvaluations.values().toArray(),
    mainFindings,
    evaluationCountsByCategory,
  );
  const hasProCategory = sortedEvaluations.some(
    c => c.section?.display_title === PRO_CATEGORY_NAME,
  );

  const { data: proSummaryScores } = useKitSummaryScores(
    hasProCategory ? (kitId ?? null) : null,
    'pro_mss',
  );

  return (
    <div className={styles.cardsContainer}>
      {sortedEvaluations.map(category => {
        const categoryName = category.section?.display_title;
        const isPro = categoryName === PRO_CATEGORY_NAME;
        const [badgeTitle, badgeState] =
          (isPro
            ? getProNavigationBadge(proSummaryScores?.[0])
            : getSectionEvaluationBadge(category.section_evaluation)) ??
          calculateNavigationBadge(
            evaluationCountsByCategory.get(categoryName ?? '') ?? {
              green: 0,
              orange: 0,
              red: 0,
              grey: 0,
            },
            isPro,
          );

        return (
          <Card key={category.id} cardBodyClassName={styles.cardContainer}>
            <div className={styles.card}>
              <Typography variant='heading-s'>
                {category.section?.display_title}
              </Typography>
              <Badge title={badgeTitle} state={badgeState} />
            </div>
          </Card>
        );
      })}
    </div>
  );
}
